import {formatFixedNumber, formatPercent} from '../../../../../../shared/numberFormat'
import {getPdfPalette} from '../../../../../../shared/pdf/pdfTheme'
import {loadContrastingTenantLogoElement} from '../../../../../../shared/pdf/pdfTenantLogo'
import * as GisDataAdapter from '../../../services/GisDataAdapter'
import {paintAmbientGeojson} from '../Ambients/ambientMapUtils'
import {getAmbientClassCount, getAmbientClassLabel} from '../Ambients/ambientClassPresentation'
import {
  ALTIMETRY_LEGEND_HEIGHT,
  ALTIMETRY_LEGEND_WIDTH,
  buildAltimetryDownloadCanvas,
  buildAltimetryRaster,
  drawAltimetryLegend,
} from '../Images/Services/Altimetry/Altimetry'
import {
  createSoilMapCanvas,
  getSoilMapLegendModel,
} from '../Images/Services/SoilMap/soilMapVisuals'
import {buildFieldLandDownloadSubject} from './downloadArchive'

const VISUAL_EXPORT_WIDTH = 1120
const VISUAL_EXPORT_PADDING = 34
const VISUAL_EXPORT_GAP = 28
const VISUAL_EXPORT_TITLE_HEIGHT = 58
const AMBIENT_LEGEND_WIDTH = 292
const SOIL_MAP_LEGEND_WIDTH = 340
const YIELD_MAP_LEGEND_WIDTH = 292
const YIELD_MAP_LEGEND_HEIGHT = 190
const PNG_EXPORT_SCALE = 4
const PNG_AMBIENT_LAYER_WIDTH = 2600
const PNG_AMBIENT_LAYER_MAX_HEIGHT = 4200
const PNG_ALTIMETRY_RASTER_SIZE = 2048
const PDF_PAGE_WIDTH = 595
const PDF_PAGE_HEIGHT = 842
const PDF_LOGICAL_WIDTH = 1240
const PDF_LOGICAL_HEIGHT = 1754
const PDF_RENDER_SCALE = 2
const PDF_CANVAS_WIDTH = PDF_LOGICAL_WIDTH * PDF_RENDER_SCALE
const PDF_CANVAS_HEIGHT = PDF_LOGICAL_HEIGHT * PDF_RENDER_SCALE

const getCenteredVisualGroupLayout = ({containerX = 0, containerWidth, imageWidth, legendWidth, gap}) => {
  const groupWidth = imageWidth + gap + legendWidth
  const groupX = containerX + Math.max(0, Math.round((containerWidth - groupWidth) / 2))

  return {
    imageX: groupX,
    legendX: groupX + imageWidth + gap,
  }
}

const cp1252Map = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
}

const drawRoundedRect = (context, x, y, width, height, radius) => {
  const normalizedRadius = Math.min(radius, width / 2, height / 2)

  context.beginPath()
  context.moveTo(x + normalizedRadius, y)
  context.lineTo(x + width - normalizedRadius, y)
  context.quadraticCurveTo(x + width, y, x + width, y + normalizedRadius)
  context.lineTo(x + width, y + height - normalizedRadius)
  context.quadraticCurveTo(x + width, y + height, x + width - normalizedRadius, y + height)
  context.lineTo(x + normalizedRadius, y + height)
  context.quadraticCurveTo(x, y + height, x, y + height - normalizedRadius)
  context.lineTo(x, y + normalizedRadius)
  context.quadraticCurveTo(x, y, x + normalizedRadius, y)
  context.closePath()
}

const getAspectFitCanvasSize = ({
  heightRatio,
  maxHeight,
  maxWidth,
  minHeight = 260,
  minWidth = 260,
}) => {
  const safeHeightRatio =
    Number.isFinite(heightRatio) && heightRatio > 0 ? heightRatio : 1
  let width = maxWidth
  let height = Math.round(width * safeHeightRatio)

  if (height > maxHeight) {
    height = maxHeight
    width = Math.round(height / safeHeightRatio)
  }

  const minScale = Math.max(minWidth / Math.max(width, 1), minHeight / Math.max(height, 1), 1)
  width = Math.round(width * minScale)
  height = Math.round(height * minScale)

  const maxScale = Math.min(maxWidth / Math.max(width, 1), maxHeight / Math.max(height, 1), 1)
  width = Math.round(width * maxScale)
  height = Math.round(height * maxScale)

  return {
    height: Math.max(1, height),
    width: Math.max(1, width),
  }
}

const isCoordinatePair = (value) =>
  Array.isArray(value) &&
  value.length >= 2 &&
  Number.isFinite(Number(value[0])) &&
  Number.isFinite(Number(value[1]))

const collectCoordinatePairs = (value, output = []) => {
  if (value?.type === 'FeatureCollection') {
    value.features?.forEach((feature) => collectCoordinatePairs(feature, output))
    return output
  }

  if (value?.type === 'Feature') {
    return collectCoordinatePairs(value.geometry, output)
  }

  if (value?.coordinates) {
    return collectCoordinatePairs(value.coordinates, output)
  }

  if (!Array.isArray(value)) {
    return output
  }

  if (isCoordinatePair(value)) {
    output.push([Number(value[0]), Number(value[1])])
    return output
  }

  value.forEach((item) => collectCoordinatePairs(item, output))
  return output
}

const getGeometryBbox = (value) => {
  const coordinates = collectCoordinatePairs(value)

  if (coordinates.length < 3) {
    return null
  }

  const lngs = coordinates.map(([lng]) => lng)
  const lats = coordinates.map(([, lat]) => lat)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)

  if (minLng === maxLng || minLat === maxLat) {
    return null
  }

  return {maxLat, maxLng, minLat, minLng}
}

const getCanvasPointFromCoordinate = ([lng, lat], bbox, width, height, padding = 0) => {
  const lngSpan = bbox.maxLng - bbox.minLng
  const latSpan = bbox.maxLat - bbox.minLat

  if (!lngSpan || !latSpan) {
    return null
  }

  return {
    x: padding + ((lng - bbox.minLng) / lngSpan) * (width - padding * 2),
    y: padding + ((bbox.maxLat - lat) / latSpan) * (height - padding * 2),
  }
}

const getAmbientFeaturePolygons = (geometry) => {
  if (geometry?.type === 'Polygon') {
    return [geometry.coordinates]
  }

  if (geometry?.type === 'MultiPolygon') {
    return geometry.coordinates
  }

  return []
}

const drawCoordinateRings = (context, value, bbox, width, height, padding = 0) => {
  if (value?.type === 'FeatureCollection') {
    value.features?.forEach((feature) =>
      drawCoordinateRings(context, feature, bbox, width, height, padding)
    )
    return
  }

  if (value?.type === 'Feature') {
    drawCoordinateRings(context, value.geometry, bbox, width, height, padding)
    return
  }

  if (value?.coordinates) {
    drawCoordinateRings(context, value.coordinates, bbox, width, height, padding)
    return
  }

  if (!Array.isArray(value)) {
    return
  }

  if (value.length >= 3 && value.every(isCoordinatePair)) {
    value.forEach((coordinate, index) => {
      const point = getCanvasPointFromCoordinate(
        [Number(coordinate[0]), Number(coordinate[1])],
        bbox,
        width,
        height,
        padding
      )

      if (!point) {
        return
      }

      if (index === 0) {
        context.moveTo(point.x, point.y)
        return
      }

      context.lineTo(point.x, point.y)
    })
    context.closePath()
    return
  }

  value.forEach((item) => drawCoordinateRings(context, item, bbox, width, height, padding))
}

const getAmbientCanvasSize = (
  bbox,
  {
    maxHeight = 900,
    width = VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2 - VISUAL_EXPORT_GAP - AMBIENT_LEGEND_WIDTH,
  } = {}
) => {
  const lngSpan = Math.abs(bbox.maxLng - bbox.minLng) || 1
  const latSpan = Math.abs(bbox.maxLat - bbox.minLat) || 1

  return getAspectFitCanvasSize({
    heightRatio: latSpan / lngSpan,
    maxHeight,
    maxWidth: width,
  })
}

const createAmbientLayerCanvas = (geojson, bbox, landGeometry = null, options = {}) => {
  if (!geojson?.features?.length || !bbox) {
    return null
  }

  const {height, width} = getAmbientCanvasSize(bbox, options)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')

  context.fillStyle = '#f4f7fb'
  context.fillRect(0, 0, width, height)

  geojson.features.forEach((feature) => {
    const color = feature.properties?.color
    const polygons = getAmbientFeaturePolygons(feature.geometry)

    if (!color || polygons.length < 1) {
      return
    }

    context.beginPath()
    polygons.forEach((polygon) => {
      drawCoordinateRings(context, polygon, bbox, width, height, 16)
    })
    context.fillStyle = color
    context.fill('evenodd')
  })

  context.strokeStyle = '#1b84ff'
  context.lineWidth = 4
  context.beginPath()
  if (landGeometry) {
    drawCoordinateRings(context, landGeometry, bbox, width, height, 16)
  } else {
    geojson.features.forEach((feature) => {
      getAmbientFeaturePolygons(feature.geometry).forEach((polygon) => {
        drawCoordinateRings(context, polygon, bbox, width, height, 16)
      })
    })
  }
  context.stroke()

  return canvas
}

const getAmbientColor = (key, numClasses) => {
  return {
    2: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(26,150,65)',
    },
    3: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(255,255,192)',
      pp: 'rgb(26,150,65)',
    },
    4: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(254,201,129)',
      pp: 'rgb(196,230,135)',
      ap: 'rgb(26,150,65)',
    },
    5: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(253,174,97)',
      pp: 'rgb(255,255,192)',
      ap: 'rgb(166,217,106)',
      map: 'rgb(26,150,65)',
    },
    6: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(246,144,83)',
      pp: 'rgb(255,223,154)',
      ap: 'rgb(220,240,158)',
      map: 'rgb(138,204,98)',
      five: 'rgb(26,150,65)',
    },
    7: {
      mbp: 'rgb(215,25,28)',
      bp: 'rgb(241,124,74)',
      pp: 'rgb(254,201,129)',
      ap: 'rgb(255,255,192)',
      map: 'rgb(196,230,135)',
      five: 'rgb(119,195,92)',
      six: 'rgb(26,150,65)',
    },
  }[parseInt(numClasses, 10)]?.[key] || '#9ca3af'
}

const getAmbientValidClasses = (numClasses) => {
  return {
    2: ['mbp', 'bp'],
    3: ['mbp', 'bp', 'pp'],
    4: ['mbp', 'bp', 'pp', 'ap'],
    5: ['mbp', 'bp', 'pp', 'ap', 'map'],
    6: ['mbp', 'bp', 'pp', 'ap', 'map', 'five'],
    7: ['mbp', 'bp', 'pp', 'ap', 'map', 'five', 'six'],
  }[numClasses] || []
}

const getAmbientLegendRows = (ambient) => {
  if (!ambient?.geojson?.features?.length) {
    return []
  }

  const base = {
    mbp: {classIndex: 0, ha: 0, sort: 6},
    bp: {classIndex: 1, ha: 0, sort: 5},
    pp: {classIndex: 2, ha: 0, sort: 4},
    ap: {classIndex: 3, ha: 0, sort: 3},
    map: {classIndex: 4, ha: 0, sort: 2},
    five: {classIndex: 5, ha: 0, sort: 1},
    six: {classIndex: 6, ha: 0, sort: 0},
  }
  let total = 0

  ambient.geojson.features.forEach((feature) => {
    const currentHa = Number(feature.properties?.sup_ha || 0)
    total += currentHa

    switch (feature.properties?.cls) {
      case 0:
        base.mbp.ha += currentHa
        break
      case 1:
        base.bp.ha += currentHa
        break
      case 2:
        base.pp.ha += currentHa
        break
      case 3:
        base.ap.ha += currentHa
        break
      case 4:
        base.map.ha += currentHa
        break
      case 5:
        base.five.ha += currentHa
        break
      case 6:
        base.six.ha += currentHa
        break
      default:
        break
    }
  })

  const numClasses = getAmbientClassCount(ambient)

  return Object.entries(base)
    .map(([key, value]) => ({
      ...value,
      color: getAmbientColor(key, numClasses),
      key,
      label: getAmbientClassLabel({classIndex: value.classIndex, classCount: numClasses}),
      percentage: total > 0 ? (value.ha * 100) / total : 0,
      total,
    }))
    .sort((left, right) => left.sort - right.sort)
    .filter((entry) => getAmbientValidClasses(numClasses).includes(entry.key))
}

const getFormattedAmbientLayerDate = (ambient) => {
  if (!ambient?.params?.layer_date) {
    return null
  }

  const date = new Date(ambient.params.layer_date)
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset())

  if (Number.isNaN(date.getTime())) {
    return null
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `EVI ${day}/${month}/${year}`
}

const getAmbientLegendHeight = (rows, hasMeta = false) => {
  const rowHeight = 22
  const firstRowTop = hasMeta ? 104 : 88
  const bottomLabelTop = firstRowTop + rows.length * rowHeight + 3
  const totalTop = bottomLabelTop + 22

  return totalTop + 30
}

const drawFittedText = (
  context,
  text,
  x,
  y,
  maxWidth,
  {color = '#101426', fontSize = 24, fontWeight = 700, minFontSize = 16} = {}
) => {
  let currentFontSize = fontSize
  let output = String(text || '')

  context.fillStyle = color
  context.textAlign = 'left'
  context.textBaseline = 'top'

  while (currentFontSize > minFontSize) {
    context.font = `${fontWeight} ${currentFontSize}px Poppins, Arial, sans-serif`

    if (context.measureText(output).width <= maxWidth) {
      break
    }

    currentFontSize -= 1
  }

  while (context.measureText(output).width > maxWidth && output.length > 1) {
    const nextOutput = output.length > 4 ? `${output.slice(0, -4)}...` : output.slice(0, -1)

    if (nextOutput === output) {
      break
    }

    output = nextOutput
  }

  context.fillText(output, x, y)
}

const getCanvasTextWidth = (context, text, fontSize) => {
  if (context?.measureText) {
    return context.measureText(String(text || '')).width
  }

  return String(text || '').length * fontSize * 0.58
}

const splitCanvasToken = (context, token, maxWidth, fontSize) => {
  const chunks = []
  let current = ''

  Array.from(String(token || '')).forEach((character) => {
    const next = `${current}${character}`

    if (current && getCanvasTextWidth(context, next, fontSize) > maxWidth) {
      chunks.push(current)
      current = character
      return
    }

    current = next
  })

  if (current) {
    chunks.push(current)
  }

  return chunks
}

const wrapCanvasText = (
  context,
  text,
  maxWidth,
  {font = '600 10px Poppins, Arial, sans-serif', fontSize = 10} = {}
) => {
  if (context) {
    context.font = font
  }

  const output = []
  const paragraphs = String(text || '').split(/\r?\n/)

  paragraphs.forEach((paragraph) => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean)

    if (!words.length) {
      output.push('')
      return
    }

    let current = ''

    words.forEach((word) => {
      const wordChunks =
        getCanvasTextWidth(context, word, fontSize) > maxWidth
          ? splitCanvasToken(context, word, maxWidth, fontSize)
          : [word]

      wordChunks.forEach((chunk) => {
        const next = current ? `${current} ${chunk}` : chunk

        if (current && getCanvasTextWidth(context, next, fontSize) > maxWidth) {
          output.push(current)
          current = chunk
          return
        }

        current = next
      })
    })

    if (current) {
      output.push(current)
    }
  })

  return output.length ? output : ['']
}

const drawCanvasTextLines = (
  context,
  lines,
  x,
  y,
  {color, font, lineHeight, textAlign = 'left'} = {}
) => {
  context.fillStyle = color
  context.font = font
  context.textAlign = textAlign
  context.textBaseline = 'top'

  lines.forEach((line, index) => {
    context.fillText(line, x, y + index * lineHeight)
  })
}

const drawAmbientLegend = (context, rows, x, y, width) => {
  const rowHeight = 22
  const metaLabel = rows.metaLabel
  const legendHeight = getAmbientLegendHeight(rows, Boolean(metaLabel))
  const headerTop = y + (metaLabel ? 66 : 50)
  const topScaleTop = headerTop + 21
  const tableTop = topScaleTop + 18
  const bottomScaleTop = tableTop + rows.length * rowHeight + 3
  const totalTop = bottomScaleTop + 22
  const contentX = x + 16
  const labelX = x + 50
  const haX = x + width - 72
  const percentX = x + width - 16

  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.10)'
  context.shadowBlur = 18
  context.shadowOffsetY = 8
  drawRoundedRect(context, x, y, width, legendHeight, 18)
  context.fillStyle = '#ffffff'
  context.fill()
  context.restore()

  drawRoundedRect(context, x, y, width, legendHeight, 18)
  context.strokeStyle = 'rgba(15, 23, 42, 0.08)'
  context.lineWidth = 1
  context.stroke()

  context.fillStyle = '#101426'
  context.font = '700 18px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  context.textAlign = 'left'
  context.fillText('Leyenda', contentX, y + 16)

  if (metaLabel) {
    context.fillStyle = '#7f87a3'
    context.font = '700 12px Poppins, Arial, sans-serif'
    context.fillText(metaLabel, contentX, y + 42)
  }

  context.fillStyle = '#101426'
  context.font = '700 11px Poppins, Arial, sans-serif'
  context.fillText('CLASE', labelX, headerTop)
  context.textAlign = 'right'
  context.fillText('HA', haX, headerTop)
  context.fillText('%', percentX, headerTop)
  context.textAlign = 'left'

  context.fillStyle = '#7f87a3'
  context.font = '700 10px Poppins, Arial, sans-serif'
  context.fillText('MAYOR PRODUCTIVIDAD', contentX, topScaleTop)

  rows.forEach((row, index) => {
    const rowY = tableTop + index * rowHeight

    context.fillStyle = row.color
    drawRoundedRect(context, contentX, rowY, 16, 16, 4)
    context.fill()

    context.fillStyle = '#172033'
    context.font = '700 11px Poppins, Arial, sans-serif'
    context.fillText(row.label || `Clase ${index + 1}`, labelX, rowY + 1)

    context.textAlign = 'right'
    context.font = '500 11px Poppins, Arial, sans-serif'
    context.fillText(formatFixedNumber(row.ha), haX, rowY + 1)
    context.fillText(formatPercent(row.percentage), percentX, rowY + 1)
    context.textAlign = 'left'
  })

  context.fillStyle = '#7f87a3'
  context.font = '700 10px Poppins, Arial, sans-serif'
  context.fillText('MENOR PRODUCTIVIDAD', contentX, bottomScaleTop)

  context.strokeStyle = '#eef1f6'
  context.lineWidth = 1
  context.beginPath()
  context.moveTo(contentX, totalTop - 7)
  context.lineTo(x + width - 16, totalTop - 7)
  context.stroke()

  const totalHa = rows.reduce((sum, row) => sum + row.ha, 0)

  context.fillStyle = '#172033'
  context.font = '700 11px Poppins, Arial, sans-serif'
  context.fillText('Total', labelX, totalTop)
  context.textAlign = 'right'
  context.fillText(formatFixedNumber(totalHa), haX, totalTop)
  context.fillText(formatPercent(100), percentX, totalTop)
  context.textAlign = 'left'

  return legendHeight
}

const drawScaledAmbientLegend = (context, rows, x, y, width, scale = 1) => {
  context.save()
  context.translate(x, y)
  context.scale(scale, scale)
  const legendHeight = drawAmbientLegend(context, rows, 0, 0, width / scale)
  context.restore()

  return legendHeight * scale
}

const drawScaledAltimetryLegend = (context, legend, x, y, width, scale = 1) => {
  context.save()
  context.translate(x, y)
  context.scale(scale, scale)
  const legendHeight = drawAltimetryLegend(context, legend, 0, 0, width / scale)
  context.restore()

  return legendHeight * scale
}

const getSoilMapMetadataRows = (legend, labels) => {
  const scales = (legend?.scales || [])
    .map((scale) => scale?.label)
    .filter(Boolean)
    .join(' / ')

  return [
    legend?.dataset ? {label: labels.dataset, value: legend.dataset} : null,
    scales ? {label: labels.scale, value: scales} : null,
    legend?.coverage?.percentage != null
      ? {
          label: labels.coverage,
          value: formatPercent(legend.coverage.percentage),
        }
      : null,
    legend?.documentation?.memory?.title
      ? {
          label: labels.memory,
          value: legend.documentation.memory.title,
        }
      : null,
    legend?.source?.name
      ? {label: labels.source, value: legend.source.name}
      : null,
  ].filter(Boolean)
}

const getSoilMapMetadataLayout = (legend, labels, width, context = null) => {
  const rows = getSoilMapMetadataRows(legend, labels)
  const contentWidth = Math.max(1, width - 32)
  const columnGap = 14
  const labelWidth = Math.min(132, Math.max(104, Math.round(contentWidth * 0.4)))
  const valueWidth = Math.max(1, contentWidth - labelWidth - columnGap)
  const lineHeight = 15
  const rowGap = 8
  const labelFont = '700 10px Poppins, Arial, sans-serif'
  const valueFont = '600 10px Poppins, Arial, sans-serif'
  const layoutRows = rows.map((row) => {
    const labelLines = wrapCanvasText(context, row.label, labelWidth, {
      font: labelFont,
      fontSize: 10,
    })
    const valueLines = wrapCanvasText(context, row.value, valueWidth, {
      font: valueFont,
      fontSize: 10,
    })

    return {
      ...row,
      height: Math.max(labelLines.length, valueLines.length) * lineHeight + rowGap,
      labelLines,
      valueLines,
    }
  })

  return {
    columnGap,
    labelFont,
    labelWidth,
    lineHeight,
    rows: layoutRows,
    totalHeight: layoutRows.reduce((sum, row) => sum + row.height, 0),
    valueFont,
    valueWidth,
  }
}

const getCanvasMeasurementContext = () => {
  if (typeof document === 'undefined') {
    return null
  }

  return document.createElement('canvas').getContext('2d')
}

export const getSoilMapLegendHeight = (legend, labels, width = SOIL_MAP_LEGEND_WIDTH) => {
  const items = legend?.items || []
  const columns = items.length > 8 ? 2 : 1
  const itemRows = Math.ceil(items.length / columns)
  const itemHeight = items.some((item) => item?.metadataLabel) ? 38 : 30
  const metadataLayout = getSoilMapMetadataLayout(
    legend,
    labels,
    width,
    getCanvasMeasurementContext()
  )

  return (
    66 +
    itemRows * itemHeight +
    (metadataLayout.rows.length ? 18 + metadataLayout.totalHeight : 0) +
    18
  )
}

export const drawSoilMapLegend = (
  context,
  legend,
  labels,
  x,
  y,
  width = SOIL_MAP_LEGEND_WIDTH
) => {
  const items = legend?.items || []
  const columns = items.length > 8 ? 2 : 1
  const itemRows = Math.ceil(items.length / columns)
  const itemHeight = items.some((item) => item?.metadataLabel) ? 38 : 30
  const columnWidth = (width - 32) / columns
  const metadataLayout = getSoilMapMetadataLayout(legend, labels, width, context)
  const legendHeight = getSoilMapLegendHeight(legend, labels, width)

  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.12)'
  context.shadowBlur = 20
  context.shadowOffsetY = 8
  drawRoundedRect(context, x, y, width, legendHeight, 18)
  context.fillStyle = '#ffffff'
  context.fill()
  context.restore()

  drawRoundedRect(context, x, y, width, legendHeight, 18)
  context.strokeStyle = 'rgba(15, 23, 42, 0.08)'
  context.lineWidth = 1
  context.stroke()

  context.fillStyle = '#101426'
  context.font = '700 18px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  context.textAlign = 'left'
  context.fillText(labels.title, x + 16, y + 16)

  items.forEach((item, index) => {
    const column = Math.floor(index / itemRows)
    const row = index % itemRows
    const itemX = x + 16 + column * columnWidth
    const itemY = y + 52 + row * itemHeight

    context.fillStyle = item.color || '#94a3b8'
    drawRoundedRect(context, itemX, itemY + 1, 17, 17, 4)
    context.fill()
    context.strokeStyle = 'rgba(15, 23, 42, 0.20)'
    context.stroke()

    drawFittedText(
      context,
      item.label || labels.unknownUnit,
      itemX + 26,
      itemY,
      columnWidth - 31,
      {
        color: '#172033',
        fontSize: 11,
        fontWeight: 600,
        minFontSize: 8,
      }
    )

    if (item.metadataLabel) {
      drawFittedText(context, item.metadataLabel, itemX + 26, itemY + 16, columnWidth - 31, {
        color: '#7f87a3',
        fontSize: 9,
        fontWeight: 600,
        minFontSize: 7,
      })
    }
  })

  if (metadataLayout.rows.length) {
    const metadataTop = y + 52 + itemRows * itemHeight + 10

    context.strokeStyle = '#eef1f6'
    context.beginPath()
    context.moveTo(x + 16, metadataTop - 5)
    context.lineTo(x + width - 16, metadataTop - 5)
    context.stroke()

    let rowY = metadataTop
    const labelX = x + 16
    const valueX = labelX + metadataLayout.labelWidth + metadataLayout.columnGap

    metadataLayout.rows.forEach((row) => {
      drawCanvasTextLines(context, row.labelLines, labelX, rowY, {
        color: '#7f87a3',
        font: metadataLayout.labelFont,
        lineHeight: metadataLayout.lineHeight,
      })
      drawCanvasTextLines(context, row.valueLines, valueX, rowY, {
        color: '#172033',
        font: metadataLayout.valueFont,
        lineHeight: metadataLayout.lineHeight,
      })
      rowY += row.height
    })
  }

  return legendHeight
}

const drawScaledSoilMapLegend = (context, legend, labels, x, y, width, scale = 1) => {
  context.save()
  context.translate(x, y)
  context.scale(scale, scale)
  const legendHeight = drawSoilMapLegend(context, legend, labels, 0, 0, width / scale)
  context.restore()

  return legendHeight * scale
}

const getYieldMapLegendValue = (legend, ...keys) => {
  for (const key of keys) {
    const numericValue = Number(legend?.[key])

    if (Number.isFinite(numericValue)) {
      return numericValue
    }
  }

  return null
}

const normalizeYieldMapLegend = (legend) => ({
  averageYieldTonsHa: getYieldMapLegendValue(
    legend,
    'averageYieldTonsHa',
    'average_yield_tons_ha'
  ),
  maxYieldTonsHa: getYieldMapLegendValue(legend, 'maxYieldTonsHa', 'max_yield_tons_ha'),
  minYieldTonsHa: getYieldMapLegendValue(legend, 'minYieldTonsHa', 'min_yield_tons_ha'),
})

const formatYieldMapLegendValue = (value) =>
  Number.isFinite(value) ? `${formatFixedNumber(value)} t/ha` : '-'

const drawYieldMapLegend = (
  context,
  legend,
  labels,
  x,
  y,
  width = YIELD_MAP_LEGEND_WIDTH
) => {
  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.14)'
  context.shadowBlur = 22
  context.shadowOffsetY = 8
  drawRoundedRect(context, x, y, width, YIELD_MAP_LEGEND_HEIGHT, 18)
  context.fillStyle = '#ffffff'
  context.fill()
  context.restore()

  drawRoundedRect(context, x, y, width, YIELD_MAP_LEGEND_HEIGHT, 18)
  context.strokeStyle = 'rgba(15, 23, 42, 0.08)'
  context.lineWidth = 1
  context.stroke()

  const contentX = x + 18
  const contentY = y + 18

  context.fillStyle = '#101426'
  context.font = '700 18px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  context.textAlign = 'left'
  context.fillText(labels.title, contentX, contentY)

  context.fillStyle = '#7f87a3'
  context.font = '700 12px Poppins, Arial, sans-serif'
  drawFittedText(context, labels.layer, contentX, contentY + 30, width - 36, {
    color: '#7f87a3',
    fontSize: 12,
    fontWeight: 700,
    minFontSize: 10,
  })

  const scaleX = contentX
  const scaleY = contentY + 62
  const scaleWidth = 12
  const scaleHeight = 84
  const gradient = context.createLinearGradient(scaleX, scaleY, scaleX, scaleY + scaleHeight)
  gradient.addColorStop(0, '#1a9641')
  gradient.addColorStop(0.27, '#a6d96a')
  gradient.addColorStop(0.52, '#ffffc0')
  gradient.addColorStop(0.75, '#fdae61')
  gradient.addColorStop(1, '#d7191c')

  drawRoundedRect(context, scaleX, scaleY, scaleWidth, scaleHeight, 999)
  context.fillStyle = gradient
  context.fill()
  context.strokeStyle = 'rgba(15, 23, 42, 0.08)'
  context.lineWidth = 1
  context.stroke()

  const valueRows = [
    {
      label: labels.highest,
      value: formatYieldMapLegendValue(legend.maxYieldTonsHa),
      y: scaleY,
    },
    {
      label: labels.average,
      value: formatYieldMapLegendValue(legend.averageYieldTonsHa),
      y: scaleY + 35,
    },
    {
      label: labels.lowest,
      value: formatYieldMapLegendValue(legend.minYieldTonsHa),
      y: scaleY + 70,
    },
  ]
  const labelX = scaleX + scaleWidth + 12
  const valueX = x + width - 18

  valueRows.forEach((row) => {
    context.fillStyle = '#5f6680'
    context.font = '500 12px Poppins, Arial, sans-serif'
    context.textAlign = 'left'
    context.textBaseline = 'middle'
    context.fillText(row.label, labelX, row.y + 7)

    context.fillStyle = '#101426'
    context.font = '700 12px Poppins, Arial, sans-serif'
    context.textAlign = 'right'
    context.fillText(row.value, valueX, row.y + 7)
  })

  context.textAlign = 'left'

  return YIELD_MAP_LEGEND_HEIGHT
}

const drawScaledYieldMapLegend = (context, legend, labels, x, y, width, scale = 1) => {
  context.save()
  context.translate(x, y)
  context.scale(scale, scale)
  const legendHeight = drawYieldMapLegend(context, legend, labels, 0, 0, width / scale)
  context.restore()

  return legendHeight * scale
}

const buildAmbientVisualExportCanvas = ({
  layerCanvas,
  legendRows,
  metaLabel,
  scale = 1,
  title,
}) => {
  const imageMaxWidth = VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2 - VISUAL_EXPORT_GAP - AMBIENT_LEGEND_WIDTH
  const imageRatio = Math.min(
    imageMaxWidth / Math.max(layerCanvas.width, 1),
    660 / Math.max(layerCanvas.height, 1),
    1
  )
  const imageWidth = Math.round(layerCanvas.width * imageRatio)
  const imageHeight = Math.round(layerCanvas.height * imageRatio)
  const legendHeight = getAmbientLegendHeight(legendRows, Boolean(metaLabel))
  const contentTop = VISUAL_EXPORT_PADDING + VISUAL_EXPORT_TITLE_HEIGHT
  const outputHeight =
    VISUAL_EXPORT_PADDING * 2 + VISUAL_EXPORT_TITLE_HEIGHT + Math.max(imageHeight, legendHeight)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(VISUAL_EXPORT_WIDTH * scale)
  canvas.height = Math.round(outputHeight * scale)
  const context = canvas.getContext('2d')

  context.scale(scale, scale)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, VISUAL_EXPORT_WIDTH, outputHeight)

  drawFittedText(
    context,
    title,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2
  )

  const {imageX, legendX} = getCenteredVisualGroupLayout({
    containerWidth: VISUAL_EXPORT_WIDTH,
    gap: VISUAL_EXPORT_GAP,
    imageWidth,
    legendWidth: AMBIENT_LEGEND_WIDTH,
  })

  context.save()
  context.fillStyle = '#f4f7fb'
  context.fillRect(imageX - 1, contentTop - 1, imageWidth + 2, imageHeight + 2)
  context.drawImage(layerCanvas, imageX, contentTop, imageWidth, imageHeight)
  context.restore()

  const legendY = contentTop + Math.max(0, Math.round((imageHeight - legendHeight) / 2))

  drawAmbientLegend(context, legendRows, legendX, legendY, AMBIENT_LEGEND_WIDTH)

  return canvas
}

const buildAmbientDownloadCanvas = ({ambient, land, layerName}) => {
  const paintedAmbient = paintAmbientGeojson(ambient)
  const ambientBbox = getGeometryBbox(paintedAmbient)
  const landBbox = getGeometryBbox(land?.polygons)
  const bbox = landBbox || ambientBbox
  const layerCanvas = createAmbientLayerCanvas(paintedAmbient, bbox, land?.polygons)
  const highResolutionLayerCanvas = createAmbientLayerCanvas(paintedAmbient, bbox, land?.polygons, {
    maxHeight: PNG_AMBIENT_LAYER_MAX_HEIGHT,
    width: PNG_AMBIENT_LAYER_WIDTH,
  })
  const legendRows = getAmbientLegendRows(ambient)
  const metaLabel = getFormattedAmbientLayerDate(ambient)
  const subject = buildFieldLandDownloadSubject({
    fieldName: land?.field?.name,
    landName: land?.name,
  })
  const title = [layerName, subject].filter(Boolean).join(' - ')
  legendRows.metaLabel = metaLabel

  if (!layerCanvas || legendRows.length < 1) {
    throw new Error('ambient-visual-export-unavailable')
  }

  const canvas = buildAmbientVisualExportCanvas({
    layerCanvas,
    legendRows,
    metaLabel,
    title,
  })

  canvas.visualLayerExport = {
    layerCanvas: highResolutionLayerCanvas || layerCanvas,
    layerName,
    legendRows,
    metaLabel,
    subject,
    title,
    type: 'ambient',
  }

  return canvas
}

const buildAltimetryVisualExportCanvas = ({metadata, scale = 1}) => {
  const layerCanvas = metadata.layerCanvas
  const title = metadata.title || [metadata.layerName, metadata.subject].filter(Boolean).join(' - ')
  const legendWidth = ALTIMETRY_LEGEND_WIDTH
  const legendHeight = ALTIMETRY_LEGEND_HEIGHT
  const imageMaxWidth = VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2 - VISUAL_EXPORT_GAP - legendWidth
  const imageRatio = Math.min(
    imageMaxWidth / Math.max(layerCanvas.width, 1),
    760 / Math.max(layerCanvas.height, 1),
    1
  )
  const imageWidth = Math.round(layerCanvas.width * imageRatio)
  const imageHeight = Math.round(layerCanvas.height * imageRatio)
  const contentTop = VISUAL_EXPORT_PADDING + VISUAL_EXPORT_TITLE_HEIGHT
  const outputHeight =
    VISUAL_EXPORT_PADDING * 2 + VISUAL_EXPORT_TITLE_HEIGHT + Math.max(imageHeight, legendHeight)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(VISUAL_EXPORT_WIDTH * scale)
  canvas.height = Math.round(outputHeight * scale)
  const context = canvas.getContext('2d')

  context.scale(scale, scale)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, VISUAL_EXPORT_WIDTH, outputHeight)

  drawFittedText(
    context,
    title,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2
  )

  const {imageX, legendX} = getCenteredVisualGroupLayout({
    containerWidth: VISUAL_EXPORT_WIDTH,
    gap: VISUAL_EXPORT_GAP,
    imageWidth,
    legendWidth,
  })

  context.save()
  context.fillStyle = '#f4f7fb'
  context.fillRect(imageX - 1, contentTop - 1, imageWidth + 2, imageHeight + 2)
  context.drawImage(layerCanvas, imageX, contentTop, imageWidth, imageHeight)
  context.restore()

  const legendY = contentTop + Math.max(0, Math.round((imageHeight - legendHeight) / 2))
  drawAltimetryLegend(context, metadata.legend, legendX, legendY, legendWidth)

  return canvas
}

const buildSoilMapVisualExportCanvas = ({metadata, scale = 1}) => {
  const layerCanvas = metadata.layerCanvas
  const title = metadata.title || [metadata.layerName, metadata.subject].filter(Boolean).join(' - ')
  const legendWidth = SOIL_MAP_LEGEND_WIDTH
  const legendHeight = getSoilMapLegendHeight(metadata.legend, metadata.labels, legendWidth)
  const imageMaxWidth =
    VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2 - VISUAL_EXPORT_GAP - legendWidth
  const imageRatio = Math.min(
    imageMaxWidth / Math.max(layerCanvas.width, 1),
    760 / Math.max(layerCanvas.height, 1),
    1
  )
  const imageWidth = Math.round(layerCanvas.width * imageRatio)
  const imageHeight = Math.round(layerCanvas.height * imageRatio)
  const contentTop = VISUAL_EXPORT_PADDING + VISUAL_EXPORT_TITLE_HEIGHT
  const outputHeight =
    VISUAL_EXPORT_PADDING * 2 + VISUAL_EXPORT_TITLE_HEIGHT + Math.max(imageHeight, legendHeight)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(VISUAL_EXPORT_WIDTH * scale)
  canvas.height = Math.round(outputHeight * scale)
  const context = canvas.getContext('2d')

  context.scale(scale, scale)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, VISUAL_EXPORT_WIDTH, outputHeight)

  drawFittedText(
    context,
    title,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2
  )

  const {imageX, legendX} = getCenteredVisualGroupLayout({
    containerWidth: VISUAL_EXPORT_WIDTH,
    gap: VISUAL_EXPORT_GAP,
    imageWidth,
    legendWidth,
  })

  context.save()
  context.fillStyle = '#f4f7fb'
  context.fillRect(imageX - 1, contentTop - 1, imageWidth + 2, imageHeight + 2)
  context.drawImage(layerCanvas, imageX, contentTop, imageWidth, imageHeight)
  context.restore()

  const legendY = contentTop + Math.max(0, Math.round((imageHeight - legendHeight) / 2))
  drawSoilMapLegend(
    context,
    metadata.legend,
    metadata.labels,
    legendX,
    legendY,
    legendWidth
  )

  return canvas
}

const buildYieldMapVisualExportCanvas = ({metadata, scale = 1}) => {
  const layerCanvas = metadata.layerCanvas
  const title = metadata.title || [metadata.layerName, metadata.subject].filter(Boolean).join(' - ')
  const imageMaxWidth =
    VISUAL_EXPORT_WIDTH -
    VISUAL_EXPORT_PADDING * 2 -
    VISUAL_EXPORT_GAP -
    YIELD_MAP_LEGEND_WIDTH
  const imageRatio = Math.min(
    imageMaxWidth / Math.max(layerCanvas.width, 1),
    720 / Math.max(layerCanvas.height, 1),
    2.6
  )
  const imageWidth = Math.round(layerCanvas.width * imageRatio)
  const imageHeight = Math.round(layerCanvas.height * imageRatio)
  const contentTop = VISUAL_EXPORT_PADDING + VISUAL_EXPORT_TITLE_HEIGHT
  const outputHeight =
    VISUAL_EXPORT_PADDING * 2 +
    VISUAL_EXPORT_TITLE_HEIGHT +
    Math.max(imageHeight, YIELD_MAP_LEGEND_HEIGHT)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(VISUAL_EXPORT_WIDTH * scale)
  canvas.height = Math.round(outputHeight * scale)
  const context = canvas.getContext('2d')

  context.scale(scale, scale)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, VISUAL_EXPORT_WIDTH, outputHeight)

  drawFittedText(
    context,
    title,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_PADDING,
    VISUAL_EXPORT_WIDTH - VISUAL_EXPORT_PADDING * 2
  )

  const {imageX, legendX} = getCenteredVisualGroupLayout({
    containerWidth: VISUAL_EXPORT_WIDTH,
    gap: VISUAL_EXPORT_GAP,
    imageWidth,
    legendWidth: YIELD_MAP_LEGEND_WIDTH,
  })

  context.save()
  context.fillStyle = '#f4f7fb'
  context.fillRect(imageX - 1, contentTop - 1, imageWidth + 2, imageHeight + 2)
  context.drawImage(layerCanvas, imageX, contentTop, imageWidth, imageHeight)
  context.restore()

  const legendY =
    contentTop + Math.max(0, Math.round((imageHeight - YIELD_MAP_LEGEND_HEIGHT) / 2))
  drawYieldMapLegend(
    context,
    metadata.legend,
    metadata.labels,
    legendX,
    legendY,
    YIELD_MAP_LEGEND_WIDTH
  )

  return canvas
}

const createCanvasFromImageBlob = async (blob) => {
  const canvas = document.createElement('canvas')

  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob)
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    canvas.getContext('2d').drawImage(bitmap, 0, 0)
    bitmap.close?.()
    return canvas
  }

  const objectUrl = URL.createObjectURL(blob)

  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image()
      element.onload = () => resolve(element)
      element.onerror = reject
      element.src = objectUrl
    })

    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    canvas.getContext('2d').drawImage(image, 0, 0)
    return canvas
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const buildYieldMapDownloadCanvas = async ({
  campaign,
  grid,
  imageBlob,
  labels,
  land,
  layerName,
}) => {
  const layerCanvas = await createCanvasFromImageBlob(imageBlob)
  const legend = normalizeYieldMapLegend(grid?.legend)
  const subject = buildFieldLandDownloadSubject({
    fieldName: land?.field?.name,
    landName: land?.name,
  })
  const resolvedLayerName = [layerName, campaign].filter(Boolean).join(' ')
  const title = [resolvedLayerName, subject].filter(Boolean).join(' - ')
  const metadata = {
    labels: {
      ...labels,
      layer: resolvedLayerName,
    },
    layerCanvas,
    layerName: resolvedLayerName,
    legend,
    subject,
    title,
    type: 'yield_map',
  }
  const canvas = buildYieldMapVisualExportCanvas({metadata})
  canvas.visualLayerExport = metadata

  return canvas
}

const buildScaledCanvasCopy = (canvas, scale = PNG_EXPORT_SCALE) => {
  const output = document.createElement('canvas')
  output.width = Math.round(canvas.width * scale)
  output.height = Math.round(canvas.height * scale)
  const context = output.getContext('2d')

  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(canvas, 0, 0, output.width, output.height)

  return output
}

const getPngExportCanvas = (canvas) => {
  const metadata = canvas?.visualLayerExport

  if (metadata?.type === 'ambient') {
    return buildAmbientVisualExportCanvas({
      layerCanvas: metadata.layerCanvas || canvas,
      legendRows: metadata.legendRows,
      metaLabel: metadata.metaLabel,
      scale: PNG_EXPORT_SCALE,
      title: metadata.title,
    })
  }

  if (metadata?.type === 'altimetry' && metadata.layerCanvas && metadata.legend) {
    return buildAltimetryVisualExportCanvas({
      metadata,
      scale: PNG_EXPORT_SCALE,
    })
  }

  if (metadata?.type === 'soil_map' && metadata.layerCanvas && metadata.legend) {
    return buildSoilMapVisualExportCanvas({
      metadata,
      scale: PNG_EXPORT_SCALE,
    })
  }

  if (metadata?.type === 'yield_map' && metadata.layerCanvas && metadata.legend) {
    return buildYieldMapVisualExportCanvas({
      metadata,
      scale: PNG_EXPORT_SCALE,
    })
  }

  return buildScaledCanvasCopy(canvas)
}

export const canvasToPngBlob = (canvas) => {
  const exportCanvas = getPngExportCanvas(canvas)

  return (
  new Promise((resolve, reject) => {
    exportCanvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
        return
      }

      reject(new Error('No pudimos generar el archivo PNG'))
    }, 'image/png')
  })
  )
}

const drawTenantLogo = async (context, x, y, maxWidth, maxHeight, options = {}) => {
  const logo = await loadContrastingTenantLogoElement(
    options.backgroundColor || (options.preferWhite ? '#1d1b2d' : '#ffffff')
  )

  if (!logo) {
    context.fillStyle = options.preferWhite ? '#ffffff' : '#101426'
    context.font = '700 46px Poppins, Arial, sans-serif'
    context.textBaseline = 'top'
    context.fillText('SpecifiC', x, y)
    return
  }

  const logoWidth = logo.naturalWidth || logo.width
  const logoHeight = logo.naturalHeight || logo.height
  const ratio = Math.min(maxWidth / logoWidth, maxHeight / logoHeight, 1)
  const width = Math.round(logoWidth * ratio)
  const height = Math.round(logoHeight * ratio)

  context.drawImage(logo, x, y + Math.round((maxHeight - height) / 2), width, height)
}

const getTenantVisualPalette = () => {
  // Share the single source of truth with the vector PDFs so every export
  // (reports + visual layers) reads the same tenant palette.
  const palette = getPdfPalette()
  const style = window.getComputedStyle?.(document.documentElement)
  const getVar = (name, fallback) => style?.getPropertyValue(name)?.trim() || fallback

  return {
    accent: palette.primary,
    background: getVar('--kt-page-bg', '#eef4f8'),
    navy: palette.navy,
    primary: palette.primary,
    softBlue: getVar('--kt-primary-light', palette.surfaceAlt),
    text: palette.ink,
    muted: palette.muted,
  }
}

const drawVisualLayerFooter = (context, palette, x, y, width) => {
  context.strokeStyle = 'rgba(15, 23, 42, 0.12)'
  context.lineWidth = 1
  context.beginPath()
  context.moveTo(x, y)
  context.lineTo(x + width, y)
  context.stroke()

  context.fillStyle = palette.primary
  drawRoundedRect(context, x, y + 9, 44, 6, 3)
  context.fill()

  context.fillStyle = palette.muted
  context.font = '700 18px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  context.textAlign = 'left'
  context.fillText('SpecifiC', x + 60, y + 6)
}

/**
 * Single composer shared by the ambient and altimetry visual-layer pages, so
 * both read like the vector reports: a rounded inset navy header (logo +
 * eyebrow + title + accent), and a white card that hugs the map + legend and
 * is centered in the free area (no empty white panel). `legend` is
 * {width, height, draw(ctx, x, y, width)} so each layer plugs in its own legend.
 */
const composeVisualLayerPage = async ({metadata, title, legend}) => {
  const page = document.createElement('canvas')
  page.width = PDF_CANVAS_WIDTH
  page.height = PDF_CANVAS_HEIGHT
  const context = page.getContext('2d')
  const palette = getTenantVisualPalette()
  const pageWidth = PDF_LOGICAL_WIDTH
  const pageHeight = PDF_LOGICAL_HEIGHT
  const margin = 72

  context.scale(PDF_RENDER_SCALE, PDF_RENDER_SCALE)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'

  context.fillStyle = palette.background
  context.fillRect(0, 0, pageWidth, pageHeight)

  // Header — rounded inset navy band (congruent with the vector reports).
  const headerX = margin
  const headerY = margin
  const headerWidth = pageWidth - margin * 2
  const headerHeight = 196
  drawRoundedRect(context, headerX, headerY, headerWidth, headerHeight, 30)
  context.fillStyle = palette.navy
  context.fill()

  const contentX = headerX + 40
  await drawTenantLogo(context, contentX, headerY + 32, 280, 54, {
    backgroundColor: palette.navy,
    preferWhite: true,
  })

  const eyebrow = String(metadata.layerName || '').toUpperCase()
  if (eyebrow) {
    context.fillStyle = palette.primary
    context.font = '700 17px Poppins, Arial, sans-serif'
    context.textBaseline = 'top'
    context.textAlign = 'left'
    context.fillText(eyebrow, contentX, headerY + 106)
  }

  const headerTitle = metadata.subject || title || metadata.layerName || ''
  drawFittedText(context, headerTitle, contentX, headerY + 130, headerWidth - 80, {
    color: '#ffffff',
    fontSize: 42,
    fontWeight: 700,
    minFontSize: 26,
  })

  context.fillStyle = palette.primary
  drawRoundedRect(context, contentX, headerY + 180, 72, 5, 2.5)
  context.fill()

  // Content card — fills the free area below the header so the page never
  // reads as half-empty; the map + legend are sized to use that whole area.
  const footerY = pageHeight - 96
  const availTop = headerY + headerHeight + 40
  const availBottom = footerY - 40

  const cardX = margin
  const cardWidth = pageWidth - margin * 2
  const cardY = availTop
  const cardHeight = availBottom - availTop
  const cardPadding = 54
  const gap = 44

  const legendWidth = legend.width
  const legendHeight = legend.height
  const layerWidth = metadata.layerCanvas.width
  const layerHeight = metadata.layerCanvas.height

  const innerX = cardX + cardPadding
  const innerY = cardY + cardPadding
  const innerWidth = cardWidth - cardPadding * 2
  const innerHeight = cardHeight - cardPadding * 2

  // Two candidate arrangements — pick whichever shows the larger map. Side
  // keeps the legend to the right (best for tall maps); stacked drops it below
  // the map (best for wide maps). Either way the card stays full-height.
  const sideRatio = Math.min(
    (innerWidth - gap - legendWidth) / Math.max(layerWidth, 1),
    innerHeight / Math.max(layerHeight, 1)
  )
  const stackRatio = Math.min(
    innerWidth / Math.max(layerWidth, 1),
    (innerHeight - gap - legendHeight) / Math.max(layerHeight, 1)
  )
  const stacked = stackRatio >= sideRatio
  const ratio = Math.min(Math.max(sideRatio, stackRatio), 2.6)
  const mapWidth = Math.round(layerWidth * ratio)
  const mapHeight = Math.round(layerHeight * ratio)

  // Card background.
  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.12)'
  context.shadowBlur = 34
  context.shadowOffsetY = 18
  drawRoundedRect(context, cardX, cardY, cardWidth, cardHeight, 34)
  context.fillStyle = '#ffffff'
  context.fill()
  context.restore()

  drawRoundedRect(context, cardX, cardY, cardWidth, cardHeight, 34)
  context.strokeStyle = 'rgba(15, 23, 42, 0.07)'
  context.lineWidth = 1
  context.stroke()

  // Lay out map + legend, centered as a group inside the card.
  let mapX
  let mapY
  let legendX
  let legendY
  if (stacked) {
    const groupHeight = mapHeight + gap + legendHeight
    const top = innerY + Math.max(0, Math.round((innerHeight - groupHeight) / 2))
    mapX = innerX + Math.round((innerWidth - mapWidth) / 2)
    mapY = top
    legendX = innerX + Math.round((innerWidth - legendWidth) / 2)
    legendY = top + mapHeight + gap
  } else {
    const groupWidth = mapWidth + gap + legendWidth
    const left = innerX + Math.max(0, Math.round((innerWidth - groupWidth) / 2))
    const contentHeight = Math.max(mapHeight, legendHeight)
    const top = innerY + Math.max(0, Math.round((innerHeight - contentHeight) / 2))
    mapX = left
    mapY = top + Math.round((contentHeight - mapHeight) / 2)
    legendX = left + mapWidth + gap
    legendY = top + Math.round((contentHeight - legendHeight) / 2)
  }

  // Framed map.
  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.10)'
  context.shadowBlur = 22
  context.shadowOffsetY = 10
  context.fillStyle = '#f4f7fb'
  context.fillRect(mapX - 2, mapY - 2, mapWidth + 4, mapHeight + 4)
  context.restore()

  context.save()
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(metadata.layerCanvas, mapX, mapY, mapWidth, mapHeight)
  context.restore()

  legend.draw(context, legendX, legendY, legendWidth)

  drawVisualLayerFooter(context, palette, margin, footerY, pageWidth - margin * 2)

  return page
}

const drawAmbientVisualLayerPdfCanvas = async ({metadata, title}) => {
  const legendScale = 1.4
  return composeVisualLayerPage({
    metadata,
    title,
    legend: {
      width: 360,
      height: getAmbientLegendHeight(metadata.legendRows, Boolean(metadata.metaLabel)) * legendScale,
      draw: (context, x, y, width) =>
        drawScaledAmbientLegend(context, metadata.legendRows, x, y, width, legendScale),
    },
  })
}

const drawAltimetryVisualLayerPdfCanvas = async ({metadata, title}) => {
  const legendScale = 1.44
  return composeVisualLayerPage({
    metadata,
    title,
    legend: {
      width: 360,
      height: ALTIMETRY_LEGEND_HEIGHT * legendScale,
      draw: (context, x, y, width) =>
        drawScaledAltimetryLegend(context, metadata.legend, x, y, width, legendScale),
    },
  })
}

const drawSoilMapVisualLayerPdfCanvas = async ({metadata, title}) => {
  const legendScale = 1.34
  const legendWidth = 440
  const legendLogicalWidth = legendWidth / legendScale

  return composeVisualLayerPage({
    metadata,
    title,
    legend: {
      width: legendWidth,
      height:
        getSoilMapLegendHeight(metadata.legend, metadata.labels, legendLogicalWidth) *
        legendScale,
      draw: (context, x, y, width) =>
        drawScaledSoilMapLegend(
          context,
          metadata.legend,
          metadata.labels,
          x,
          y,
          width,
          legendScale
        ),
    },
  })
}

const drawYieldMapVisualLayerPdfCanvas = async ({metadata, title}) => {
  const legendScale = 1.44
  return composeVisualLayerPage({
    metadata,
    title,
    legend: {
      width: 390,
      height: YIELD_MAP_LEGEND_HEIGHT * legendScale,
      draw: (context, x, y, width) =>
        drawScaledYieldMapLegend(
          context,
          metadata.legend,
          metadata.labels,
          x,
          y,
          width,
          legendScale
        ),
    },
  })
}

export const buildVisualLayerPdfCanvas = async ({canvas, fieldName, landName, title}) => {
  if (canvas?.visualLayerExport?.type === 'ambient') {
    return drawAmbientVisualLayerPdfCanvas({
      metadata: canvas.visualLayerExport,
      title,
    })
  }

  if (canvas?.visualLayerExport?.type === 'altimetry') {
    return drawAltimetryVisualLayerPdfCanvas({
      metadata: canvas.visualLayerExport,
      title,
    })
  }

  if (canvas?.visualLayerExport?.type === 'soil_map') {
    return drawSoilMapVisualLayerPdfCanvas({
      metadata: canvas.visualLayerExport,
      title,
    })
  }

  if (canvas?.visualLayerExport?.type === 'yield_map') {
    return drawYieldMapVisualLayerPdfCanvas({
      metadata: canvas.visualLayerExport,
      title,
    })
  }

  const subject = buildFieldLandDownloadSubject({fieldName, landName})
  const page = document.createElement('canvas')
  page.width = PDF_CANVAS_WIDTH
  page.height = PDF_CANVAS_HEIGHT
  const context = page.getContext('2d')
  const padding = 82
  const pageWidth = PDF_LOGICAL_WIDTH
  const pageHeight = PDF_LOGICAL_HEIGHT

  context.scale(PDF_RENDER_SCALE, PDF_RENDER_SCALE)

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, pageWidth, pageHeight)

  await drawTenantLogo(context, padding, 58, 260, 78)

  context.fillStyle = '#101426'
  context.font = '700 40px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  context.fillText(title, padding, 176)

  context.fillStyle = '#7f87a3'
  context.font = '600 24px Poppins, Arial, sans-serif'
  context.fillText(subject, padding, 230)

  context.strokeStyle = '#e5e9f2'
  context.lineWidth = 2
  context.beginPath()
  context.moveTo(padding, 292)
  context.lineTo(pageWidth - padding, 292)
  context.stroke()

  const maxImageWidth = pageWidth - padding * 2
  const maxImageHeight = pageHeight - 380
  const imageRatio = Math.min(
    maxImageWidth / Math.max(canvas.width, 1),
    maxImageHeight / Math.max(canvas.height, 1),
    1
  )
  const imageWidth = Math.round(canvas.width * imageRatio)
  const imageHeight = Math.round(canvas.height * imageRatio)
  const imageX = padding + Math.round((maxImageWidth - imageWidth) / 2)
  const imageY = 332

  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.12)'
  context.shadowBlur = 24
  context.shadowOffsetY = 10
  drawRoundedRect(context, imageX, imageY, imageWidth, imageHeight, 24)
  context.fillStyle = '#ffffff'
  context.fill()
  context.restore()

  context.drawImage(canvas, imageX, imageY, imageWidth, imageHeight)

  return page
}

const getJpegImageHex = (canvas) => {
  const dataUrl = canvas.toDataURL('image/jpeg', 0.98)
  const binary = atob(String(dataUrl).split(',')[1] || '')
  let hex = ''

  for (let index = 0; index < binary.length; index += 1) {
    hex += binary.charCodeAt(index).toString(16).padStart(2, '0').toUpperCase()
  }

  return {
    height: canvas.height,
    hex,
    width: canvas.width,
  }
}

const escapePdfText = (value) => {
  return Array.from(String(value || '')).map((char) => {
    const code = char.codePointAt(0)
    const byte = code <= 0xff ? code : cp1252Map[code] || 0x3f
    return byte.toString(16).padStart(2, '0').toUpperCase()
  }).join('')
}

export const canvasToPdfBlob = (canvas, title = 'Capa') => {
  const image = getJpegImageHex(canvas)
  const objects = []

  objects[1] = '<< /Type /Catalog /Pages 2 0 R >>'
  objects[2] = '<< /Type /Pages /Kids [3 0 R] /Count 1 >>'
  objects[4] = `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${image.hex.length + 1} >>\nstream\n${image.hex}>\nendstream`

  const contentStream = [
    `q ${PDF_PAGE_WIDTH} 0 0 ${PDF_PAGE_HEIGHT} 0 0 cm /Im1 Do Q`,
    `BT /F1 1 Tf 1 1 1 rg 1 0 0 1 0 0 Tm <${escapePdfText(title)}> Tj ET`,
  ].join('\n')

  objects[5] = `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`
  objects[6] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>'
  objects[3] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources << /Font << /F1 6 0 R >> /XObject << /Im1 4 0 R >> >> /Contents 5 0 R >>`

  let pdf = '%PDF-1.4\n'
  const offsets = [0]

  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`
  }

  const xrefOffset = pdf.length
  pdf += `xref\n0 ${objects.length}\n`
  pdf += '0000000000 65535 f \n'

  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`
  }

  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  return new Blob([pdf], {type: 'application/pdf'})
}

export const buildVisualLayerCanvas = async ({
  canAccessHistoricalHd = false,
  land,
  labels = {},
  layer,
  layerName,
  planetHdImage = null,
}) => {
  if (layer === 'ambient_hd' && !canAccessHistoricalHd) {
    throw new Error('planet_hd_historical_internal_only')
  }
  if (layer === 'planet_hd_image') {
    const acquisitionId = planetHdImage?.acquisitionId
    const sourceLayer = planetHdImage?.layer || 'visible'

    if (!acquisitionId) {
      throw new Error('visual-layer-missing-planet-hd-acquisition')
    }

    const response = await GisDataAdapter.getLandHdAcquisitionLayer(
      land.land_id,
      acquisitionId,
      sourceLayer
    )

    if (!response?.ok || !response.data) {
      throw new Error('visual-layer-missing-planet-hd-preview')
    }

    const canvas = await createCanvasFromImageBlob(response.data)
    canvas.visualLayerExport = {
      acquisitionId,
      layer: sourceLayer,
      layerName,
      type: 'planet_hd_image',
    }

    return canvas
  }

  if (layer === 'altimetry') {
    const raster = await buildAltimetryRaster(land.polygons, {
      maxRasterSize: PNG_ALTIMETRY_RASTER_SIZE,
    })
    const subject = buildFieldLandDownloadSubject({
      fieldName: land?.field?.name,
      landName: land?.name,
    })
    const title = [layerName, subject].filter(Boolean).join(' - ')

    return buildAltimetryDownloadCanvas(raster, {
      layerName,
      subject,
      title,
    })
  }

  if (layer === 'soil_map') {
    const response = await GisDataAdapter.getLandSoilMap(land.land_id)
    const soilMap = response?.data
    const layerCanvas = createSoilMapCanvas(soilMap?.featureCollection)

    if (!response?.ok || soilMap?.status !== 'available' || !layerCanvas) {
      throw new Error('visual-layer-missing-soil-map')
    }

    const subject = buildFieldLandDownloadSubject({
      fieldName: land?.field?.name,
      landName: land?.name,
    })
    const title = [layerName, subject].filter(Boolean).join(' - ')
    const metadata = {
      labels,
      layerCanvas,
      layerName,
      legend: getSoilMapLegendModel(soilMap),
      subject,
      title,
      type: 'soil_map',
    }
    const canvas = buildSoilMapVisualExportCanvas({metadata})
    canvas.visualLayerExport = metadata

    return canvas
  }

  const response = layer === 'image_ambient_hd'
    ? await GisDataAdapter.getLandImageAmbientHd(land.land_id)
    : layer === 'ambient_hd'
      ? await GisDataAdapter.getLandAmbientHd(land.land_id)
      : layer === 'image_ambient'
        ? await GisDataAdapter.getLandImageAmbient(land.land_id)
        : await GisDataAdapter.getLandAmbient(land.land_id)

  if (!response?.ok || !response.data) {
    throw new Error('visual-layer-missing-ambient')
  }

  return buildAmbientDownloadCanvas({
    ambient: response.data,
    land,
    layerName,
  })
}
